import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import MainLayout from "../../shared/components/MainLayout";
import axios, { AxiosResponse } from "axios";

// Importiamo le icone di React Icons
import {
  FaShoppingCart,
  FaExclamationTriangle,
  FaSyncAlt,
  FaTrashAlt,
  FaClock,
} from "react-icons/fa";
import Loader from "../../shared/components/loader/Loader";
import Papa from "papaparse"; // Importa la libreria per generare il CSV
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import Button2 from "../../shared/components/button/Button2";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";

// Registriamo i componenti necessari di Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const MonitorPurchasePage = () => {
  const [rawPurchaseData, setRawPurchaseData] = useState<any[]>([]);
  const [rawErrorData, setRawErrorData] = useState<any[]>([]);
  const [rawUpdateData, setRawUpdateData] = useState<any[]>([]);
  const [rawCancelData, setRawCancelData] = useState<any[]>([]);
  const [rawPendingData, setRawPendingData] = useState<any[]>([]);

  const [purchaseData, setPurchaseData] = useState<any[]>([]);
  const [errorData, setErrorData] = useState<any[]>([]);
  const [updateData, setUpdateData] = useState<any[]>([]);
  const [cancelData, setCancelData] = useState<any[]>([]);
  const [pendingData, setPendingData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const [selectedChart, setSelectedChart] = useState<string | null>(null); // Stato per gestire il popup
  const [selectedFilter, setSelectedFilter] = useState<number>(365); // Default è 7 giorni

  const [viewMode, setViewMode] = useState<"chart" | "table">("chart");

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentPagePurchase, setCurrentPagePurchase] = useState(1);
  const [currentPageError, setCurrentPageError] = useState(1);
  const [currentPageUpdate, setCurrentPageUpdate] = useState(1);
  const [currentPageCancel, setCurrentPageCancel] = useState(1);
  const [currentPagePending, setCurrentPagePending] = useState(1);

  const navigate = useNavigate();

  const handleRedirect = () => {
    navigate("/private/monitorSubscription");
  };

  const itemsPerPage = 10; // Numero di elementi per pagina per ogni tabella

  // Funzione per ottenere i dati della pagina corrente per ogni tabella
  const getCurrentPageData = (data: any[], currentPage: number) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  // Funzione per ottenere il numero totale di pagine per ogni tabella
  const totalPages = (data: any[]) => {
    return Math.ceil(data.length / itemsPerPage);
  };

  // Funzione per gestire la paginazione
  const handlePagination = (direction: "next" | "prev", type: string) => {
    if (type === "purchase") {
      if (
        direction === "next" &&
        currentPagePurchase < totalPages(purchaseData)
      ) {
        setCurrentPagePurchase(currentPagePurchase + 1);
      } else if (direction === "prev" && currentPagePurchase > 1) {
        setCurrentPagePurchase(currentPagePurchase - 1);
      }
    } else if (type === "error") {
      if (direction === "next" && currentPageError < totalPages(errorData)) {
        setCurrentPageError(currentPageError + 1);
      } else if (direction === "prev" && currentPageError > 1) {
        setCurrentPageError(currentPageError - 1);
      }
    } else if (type === "update") {
      if (direction === "next" && currentPageUpdate < totalPages(updateData)) {
        setCurrentPageUpdate(currentPageUpdate + 1);
      } else if (direction === "prev" && currentPageUpdate > 1) {
        setCurrentPageUpdate(currentPageUpdate - 1);
      }
    } else if (type === "cancel") {
      if (direction === "next" && currentPageCancel < totalPages(cancelData)) {
        setCurrentPageCancel(currentPageCancel + 1);
      } else if (direction === "prev" && currentPageCancel > 1) {
        setCurrentPageCancel(currentPageCancel - 1);
      }
    } else if (type === "pending") {
      if (
        direction === "next" &&
        currentPagePending < totalPages(pendingData)
      ) {
        setCurrentPagePending(currentPagePending + 1);
      } else if (direction === "prev" && currentPagePending > 1) {
        setCurrentPagePending(currentPagePending - 1);
      }
    }
  };
  const getDateXDaysAgo = (days: number) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split("T")[0]; // Ritorna la data nel formato "YYYY-MM-DD"
  };

  const filterDataByDate = (
    data: any[],
    startDate: Date | null,
    endDate: Date | null
  ) => {
    return data.filter((item) => {
      const itemDate = new Date(item.date);
      if (startDate && itemDate < startDate) return false;
      if (endDate && itemDate > endDate) return false;
      return true;
    });
  };
  const fetchMonitorData = async () => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL; // Assicurati di aver configurato questa variabile nel file .env
    const URL = `${baseUrl}/nexum-saas/monitorPurchase`; // Cambia con il path corretto del tuo backend

    const token = localStorage.getItem("token");

    const headers = token
      ? {
          Authorization: `Bearer ${token}`, // Aggiungi il token come intestazione
          "Content-Type": "application/json", // Imposta il Content-Type se necessario
        }
      : {
          "Content-Type": "application/json",
        };

    const body = {
      path: "monitoring", // Body della richiesta POST
    };

    return axios
      .post(URL, body, { headers }) // Chiamata in GET senza parametri
      .then((response: AxiosResponse) => response.data) // Restituisce i dati della risposta
      .catch((error) => {
        // console.error("Errore nella chiamata API:", error.message);
        throw new Error("Failed to fetch monitor data");
      });
  };

  useEffect(() => {
    const currentDate = new Date();
    const pastDate = new Date();
    pastDate.setFullYear(currentDate.getFullYear() - 1); // 365 giorni fa

    setStartDate(pastDate); // Imposta la data di inizio a 365 giorni fa
    setEndDate(currentDate);

    const fetchData = async () => {
      try {
        const data = await fetchMonitorData();
        // console.log('Dati ricevuti dal BE:', data);
        // Salviamo i dati grezzi (non filtrati)
        setRawPurchaseData(data.payments || []);
        setRawErrorData(data.errors || []);
        setRawUpdateData(data.updates || []);
        setRawPendingData(data.pending || []);
        setRawCancelData(data.cancellations || []);

        const filteredPurchases = filterDataByDate(
          data.payments || [],
          pastDate,
          currentDate
        );
        const filteredErrors = filterDataByDate(
          data.errors || [],
          pastDate,
          currentDate
        );
        const filteredUpdates = filterDataByDate(
          data.updates || [],
          pastDate,
          currentDate
        );
        const filteredPendings = filterDataByDate(
          data.pending || [],
          pastDate,
          currentDate
        );
        const filteredCancellations = filterDataByDate(
          data.cancellations || [],
          pastDate,
          currentDate
        );

        // Impostiamo i dati filtrati
        setPurchaseData(filteredPurchases);
        setErrorData(filteredErrors);
        setUpdateData(filteredUpdates);
        setPendingData(filteredPendings);
        setCancelData(filteredCancellations);
      } catch (error) {
        //console.error("Errore nel caricamento dei dati:", error);
        throw new Error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchMonitorData();
        setRawPurchaseData(data.payments || []);
        setRawErrorData(data.errors || []);
        setRawUpdateData(data.updates || []);
        setRawPendingData(data.pending || []);
        setRawCancelData(data.cancellations || []);
      } catch (error) {
        //console.error("Errore nel caricamento dei dati:", error);
        throw new Error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <Loader />;
  }

  const createChartData = (
    data: any[],
    label: string,
    borderColor: string,
    backgroundColor: string
  ) => {
    // Ottieni le date uniche dai dati
    const dates = data.map((item) => item.date);
    const uniqueDates = Array.from(new Set(dates)); // Rimuovi i duplicati

    // Crea un array di tutte le date nell'intervallo desiderato (per esempio, gli ultimi 7 giorni)
    const startDate = getDateXDaysAgo(selectedFilter);
    const dateRange = [];
    for (let i = 0; i < selectedFilter; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dateRange.push(date.toISOString().split("T")[0]); // Aggiungi la data nel formato "YYYY-MM-DD"
    }

    // Ordinamento per garantire che la data più vecchia sia a sinistra
    dateRange.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    // Crea un oggetto di mappatura per associare le date ai contatori
    const dataMap: { [key: string]: number } = {};
    data.forEach((item) => {
      dataMap[item.date] = item.count;
    });

    // Mappa i dati per le date del range, riempiendo con 0 se non ci sono dati
    const counts = dateRange.map((date) => dataMap[date] || 0);

    return {
      labels: dateRange, // Usa sempre il range completo di date
      datasets: [
        {
          label: label,
          data: counts,
          borderColor: borderColor,
          backgroundColor: backgroundColor,
          fill: false,
          pointStyle: "circle",
          pointRadius: 5,
          pointHoverRadius: 7,
          borderWidth: 0.7,
          tension: 0.1,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: any) => `${tooltipItem.raw} units`,
        },
      },
    },
    scales: {
      x: {
        // L'asse X rappresenta le date, e con il sort effettuato nella funzione `createChartData`,
        // le date saranno già ordinate dal più vecchio al più recente.
      },
      y: {
        min: 0, // Impedisce che l'asse Y scenda sotto 0
        beginAtZero: true, // Rende l'asse Y che parte da 0
        ticks: {
          stepSize: 1, // Passo di incremento di 1
          precision: 0, // Rimuove i decimali
        },
      },
    },
  };

  const handleChartClick = (chartName: string) => {
    setSelectedChart(chartName);
  };

  const handleClosePopup = () => {
    setSelectedChart(null);
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedFilter(parseInt(e.target.value));
  };

  const filteredPurchaseData = purchaseData.filter((item) => {
    const itemDate = new Date(item.date);
    if (startDate && itemDate < startDate) return false;
    if (endDate && itemDate > endDate) return false;
    return true;
  });

  const errorTypes = [
    "Parameter invalid empty",
    "Parameter unknown",
    "Invalid request",
    "Payment failed",
    "Payment requires action",
    "Payment canceled",
    "Payment processing",
  ];

  const createErrorChartData = (
    data: any[],
    errorTypes: string[],
    label: string
  ) => {
    // Ottieni le date uniche dai dati
    const dates = data.map((item) => item.date);
    const uniqueDates = Array.from(new Set(dates)); // Rimuovi i duplicati

    // Crea un array di tutte le date nell'intervallo desiderato (per esempio, gli ultimi 7 giorni)
    const startDate = getDateXDaysAgo(selectedFilter);
    const dateRange: string[] = [];
    for (let i = 0; i < selectedFilter; i++) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      dateRange.push(date.toISOString().split("T")[0]); // Aggiungi la data nel formato "YYYY-MM-DD"
    }

    // Ordinamento per garantire che la data più vecchia sia a sinistra
    dateRange.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    // Mappa i dati per i diversi tipi di errore
    const errorDataMap: { [key: string]: { [key: string]: number } } = {};

    // Inizializza una mappa per ogni tipo di errore
    errorTypes.forEach((errorType) => {
      errorDataMap[errorType] = {};
    });

    data.forEach((item) => {
      if (errorTypes.includes(item.errorType)) {
        if (!errorDataMap[item.errorType][item.date]) {
          errorDataMap[item.errorType][item.date] = 0;
        }
        errorDataMap[item.errorType][item.date] += item.count;
      }
    });

    // Crea un array di datasets per ogni tipo di errore
    const datasets = errorTypes.map((errorType, index) => {
      const counts = dateRange.map(
        (date) => errorDataMap[errorType][date] || 0
      );
      return {
        label: `${errorType}`,
        data: counts,
        borderColor: `rgba(${(index * 50 + 100) % 255}, ${(index * 100 + 50) % 255}, ${(index * 150 + 200) % 255}, 1)`, // Colore dinamico per ogni linea
        backgroundColor: `rgba(${(index * 50 + 100) % 255}, ${(index * 100 + 50) % 255}, ${(index * 150 + 200) % 255}, 0.2)`,
        fill: false,
        pointStyle: "circle",
        pointRadius: 5,
        pointHoverRadius: 7,
        borderWidth: 0.5,
        tension: 0.1,
      };
    });

    return {
      labels: dateRange, // Usa sempre il range completo di date
      datasets: datasets,
    };
  };

  const exportToExcel = () => {
    const sheetData = (data: any[], title: string) => {
      const header = [
        ["Type", title],
        ["Date", "Count"],
      ];
      const rows = data.map((item) => [new Date(item.date), item.count]);
      return [...header, ...rows, []];
    };

    const allData = [
      ...sheetData(purchaseData, "Purchases"),
      ...sheetData(errorData, "Errors"),
      ...sheetData(updateData, "Updates"),
      ...sheetData(cancelData, "Cancellations"),
      ...sheetData(pendingData, "Pending Cancellations"),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(allData);

    const range = XLSX.utils.decode_range(worksheet["!ref"] || "");

    // Applica bordi e formato a ogni cella
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
        if (!worksheet[cellRef]) worksheet[cellRef] = { v: "" };

        // Stile con bordi e allineamento
        worksheet[cellRef].s = {
          font: { bold: row === range.s.r || col === range.s.c }, // Grassetto per intestazioni
          alignment: {
            vertical: "center",
            horizontal: col === 0 ? "center" : "right",
          }, // Date al centro
          border: {
            top: { style: "medium", color: { rgb: "000000" } },
            bottom: { style: "medium", color: { rgb: "000000" } },
            left: { style: "medium", color: { rgb: "000000" } },
            right: { style: "medium", color: { rgb: "000000" } },
          },
        };

        // Formatta la colonna Date come "dd/mm/yyyy"
        if (col === 0 && row > 1) {
          worksheet[cellRef].z = "dd/mm/yyyy";
        }
      }
    }

    // Adatta larghezza delle colonne
    worksheet["!cols"] = [
      { wch: 12 }, // Colonna Date: larghezza fissa
      { wch: 10 }, // Colonna Count: larghezza più stretta
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Monitor Data");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      "monitor_data_export.xlsx"
    );
  };

  const handleSearch = () => {
    const filteredPurchaseData = filterDataByDate(
      rawPurchaseData,
      startDate,
      endDate
    );
    const filteredErrorData = filterDataByDate(
      rawErrorData,
      startDate,
      endDate
    );
    const filteredUpdateData = filterDataByDate(
      rawUpdateData,
      startDate,
      endDate
    );
    const filteredCancelData = filterDataByDate(
      rawCancelData,
      startDate,
      endDate
    );
    const filteredPendingData = filterDataByDate(
      rawPendingData,
      startDate,
      endDate
    );

    setPurchaseData(filteredPurchaseData);
    setErrorData(filteredErrorData);
    setUpdateData(filteredUpdateData);
    setCancelData(filteredCancelData);
    setPendingData(filteredPendingData);
  };

  if (loading) {
    return <Loader></Loader>;
  }

  return (
    <MainLayout>
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Monitor Purchase
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Track your purchases, errors, updates, cancellations, and pending
          cancellations.
        </p>
      </div>

      <div className="flex justify-center mb-6">
        <button
          className={`py-2 px-6 rounded-l-lg ${viewMode === "chart" ? "bg-green-600 text-white" : "bg-gray-300 text-gray-700"}`}
          onClick={() => setViewMode("chart")}
        >
          Graph View
        </button>
        <button
          className={`py-2 px-6 rounded-r-lg ${viewMode === "table" ? "bg-green-600 text-white" : "bg-gray-300 text-gray-700"}`}
          onClick={() => setViewMode("table")}
        >
          Table View
        </button>
      </div>
      <div className="flex justify-between items-center mb-6 mx-4 sm:mx-8 lg:mx-16">
        {/* Sezione Start Date ed End Date */}
        {viewMode === "table" && (
          <div className="flex gap-10">
            <div>
              <label
                htmlFor="startDate"
                className="block text-lg font-semibold"
              >
                Start Date
              </label>
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                className="p-2 border border-gray-400 rounded-lg"
                placeholderText="Seleziona una data"
                isClearable
              />
            </div>
            <div>
              <label htmlFor="endDate" className="block text-lg font-semibold">
                End Date
              </label>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                className="p-2 border border-gray-400 rounded-lg"
                placeholderText="Seleziona una data"
                isClearable
              />
            </div>
            <Button2
              title="Filter"
              onClick={handleSearch}
              isLoading={loading}
              disabled={loading}
              className="self-end px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            />
          </div>
        )}
        {/* Filtro per selezionare l'intervallo di tempo */}
        {viewMode === "chart" && (
          <div className="flex gap-10">
            <select
              value={selectedFilter}
              onChange={handleFilterChange}
              className="p-2 border rounded"
            >
              <option value={7}>Last 7 Days</option>
              <option value={30}>Last 30 Days</option>
              <option value={90}>Last 90 Days</option>
              <option value={365}>Last year</option>
            </select>
          </div>
        )}

        {/* Bottoni Export e Monitor Subscription */}
        <div className="flex gap-10">
          <Button2
            title="Export to xlsx"
            onClick={exportToExcel}
            isLoading={loading}
            disabled={loading}
            className="py-2 px-6 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
          />
          <Button2
            title="Monitor Clients"
            onClick={handleRedirect}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-green-700 transition"
          />
        </div>
      </div>

      {viewMode === "chart" && (
        <>
          {/* Griglia di grafici */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mx-4 sm:mx-8 lg:mx-16">
            {/* <button
                    className="absolute top-4 right-4 text-sm text-blue-600"
                    onClick={() => handleExport("purchase")}
                >
                    Export CSV
                </button> */}
            {/* Grafico degli acquisti */}
            <div
              className="bg-white shadow-md rounded-xl p-6 mb-6 relative cursor-pointer"
              onClick={() => handleChartClick("purchase")}
            >
              <div className="absolute top-4 right-4 text-3xl text-green-500">
                <FaShoppingCart />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Purchases
              </h2>
              <div className="w-full h-64 mt-4">
                <Line
                  data={createChartData(
                    purchaseData,
                    "Number of Purchases",
                    "#4CAF50",
                    "rgba(76, 175, 80, 0.2)"
                  )}
                  options={chartOptions}
                />
              </div>
            </div>

            {/* Grafico degli aggiornamenti */}
            <div
              className="bg-white shadow-md rounded-xl p-6 mb-6 relative cursor-pointer"
              onClick={() => handleChartClick("update")}
            >
              <div className="absolute top-4 right-4 text-3xl text-blue-500">
                <FaSyncAlt />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900">Updates </h2>
              <div className="w-full h-64 mt-4">
                <Line
                  data={createChartData(
                    updateData,
                    "Number of Updates",
                    "#2196F3",
                    "rgba(33, 150, 243, 0.2)"
                  )}
                  options={chartOptions}
                />
              </div>
            </div>

            {/* Grafico delle cancellazioni */}
            <div
              className="bg-white shadow-md rounded-xl p-6 mb-6 relative cursor-pointer"
              onClick={() => handleChartClick("cancel")}
            >
              <div className="absolute top-4 right-4 text-3xl text-purple-500">
                <FaTrashAlt />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Cancellations{" "}
              </h2>
              <div className="w-full h-64 mt-4">
                <Line
                  data={createChartData(
                    cancelData,
                    "Number of Cancellations",
                    "#9C27B0",
                    "rgba(156, 39, 176, 0.2)"
                  )}
                  options={chartOptions}
                />
              </div>
            </div>

            {/* Grafico degli ordini in sospeso */}
            <div
              className="bg-white shadow-md rounded-xl p-6 mb-6 relative cursor-pointer"
              onClick={() => handleChartClick("pending")}
            >
              <div className="absolute top-4 right-4 text-3xl text-gray-500">
                <FaClock />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900">
                Pending Cancellations{" "}
              </h2>
              <div className="w-full h-64 mt-4">
                <Line
                  data={createChartData(
                    pendingData,
                    "Number of Pending Cancellations",
                    "#9E9E9E",
                    "rgba(158, 158, 158, 0.2)"
                  )}
                  options={chartOptions}
                />
              </div>
            </div>
          </div>

          {/* Grafico degli errori */}
          <div
            className="bg-white shadow-md rounded-xl p-6 mb-6 relative cursor-pointer ml-20 mr-20"
            onClick={() => handleChartClick("error")}
          >
            <div className="absolute top-4 right-4 text-3xl text-red-500">
              <FaExclamationTriangle />
            </div>
            <h2 className="text-2xl font-semibold text-gray-900">Errors </h2>
            <div className="w-full h-64 mt-4">
              {/* <Line data={createChartData(errorData, "Number of Errors", "#F44336", "rgba(244, 67, 54, 0.2)")} options={chartOptions} /> */}
              <Line
                data={createErrorChartData(
                  errorData,
                  errorTypes,
                  "Error Types"
                )}
                options={chartOptions}
              />
            </div>
          </div>
        </>
      )}
      {viewMode === "table" && (
        <>
          <div>
            {/* Container per le tabelle con due tabelle per riga */}
            <div className="flex flex-wrap gap-8 px-8 py-4">
              {/* Tabella per gli acquisti */}
              <div className="w-full md:w-1/2 lg:w-1/4">
                <div className="overflow-x-auto mb-8 shadow-lg rounded-lg border border-gray-900 bg-white">
                  <h3 className="text-xl font-semibold mb-4 px-6">Purchase</h3>
                  <table className="min-w-full table-auto border-collapse">
                    <thead className="bg-gray-800 text-white">
                      <tr>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Date
                        </th>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getCurrentPageData(
                        purchaseData,
                        currentPagePurchase
                      ).map((item, index) => (
                        <tr key={index} className="hover:bg-gray-100">
                          <td className="border-b border-black px-6 py-3">
                            {new Date(item.date).toLocaleDateString()}
                          </td>
                          <td className="border-b border-black px-6 py-3">
                            {item.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => handlePagination("prev", "purchase")}
                      disabled={currentPagePurchase === 1}
                      className="px-4 py-2 bg-gray-800 text-white rounded-l-md"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 bg-gray-200 text-black">
                      {currentPagePurchase} / {totalPages(purchaseData)}
                    </span>
                    <button
                      onClick={() => handlePagination("next", "purchase")}
                      disabled={
                        currentPagePurchase === totalPages(purchaseData)
                      }
                      className="px-4 py-2 bg-gray-800 text-white rounded-r-md"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabella per gli errori */}
              <div className="w-full md:w-1/2 lg:w-1/4">
                <div className="overflow-x-auto mb-8 shadow-lg rounded-lg border border-gray-900 bg-white">
                  <h3 className="text-xl font-semibold mb-4 px-6">Errors</h3>
                  <table className="min-w-full table-auto border-collapse">
                    <thead className="bg-gray-800 text-white">
                      <tr>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Date
                        </th>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Count
                        </th>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getCurrentPageData(errorData, currentPageError).map(
                        (item, index) => (
                          <tr key={index} className="hover:bg-gray-100">
                            <td className="border-b border-black px-6 py-3">
                              {new Date(item.date).toLocaleDateString()}
                            </td>
                            <td className="border-b border-black px-6 py-3">
                              {item.count}
                            </td>
                            <td className="border-b border-black px-6 py-3">
                              {item.description}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => handlePagination("prev", "error")}
                      disabled={currentPageError === 1}
                      className="px-4 py-2 bg-gray-800 text-white rounded-l-md"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 bg-gray-200 text-black">
                      {currentPageError} / {totalPages(errorData)}
                    </span>
                    <button
                      onClick={() => handlePagination("next", "error")}
                      disabled={currentPageError === totalPages(errorData)}
                      className="px-4 py-2 bg-gray-800 text-white rounded-r-md"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabella per gli aggiornamenti */}
              <div className="w-full md:w-1/2 lg:w-1/4">
                <div className="overflow-x-auto mb-8 shadow-lg rounded-lg border border-gray-900 bg-white">
                  <h3 className="text-xl font-semibold mb-4 px-6">Updates</h3>
                  <table className="min-w-full table-auto border-collapse">
                    <thead className="bg-gray-800 text-white">
                      <tr>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Date
                        </th>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getCurrentPageData(updateData, currentPageUpdate).map(
                        (item, index) => (
                          <tr key={index} className="hover:bg-gray-100">
                            <td className="border-b border-black px-6 py-3">
                              {new Date(item.date).toLocaleDateString()}
                            </td>
                            <td className="border-b border-black px-6 py-3">
                              {item.count}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => handlePagination("prev", "update")}
                      disabled={currentPageUpdate === 1}
                      className="px-4 py-2 bg-gray-800 text-white rounded-l-md"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 bg-gray-200 text-black">
                      {currentPageUpdate} / {totalPages(updateData)}
                    </span>
                    <button
                      onClick={() => handlePagination("next", "update")}
                      disabled={currentPageUpdate === totalPages(updateData)}
                      className="px-4 py-2 bg-gray-800 text-white rounded-r-md"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabella per le cancellazioni */}
              <div className="w-full md:w-1/2 lg:w-1/4">
                <div className="overflow-x-auto mb-8 shadow-lg rounded-lg border border-gray-900 bg-white">
                  <h3 className="text-xl font-semibold mb-4 px-6">
                    Cancellations
                  </h3>
                  <table className="min-w-full table-auto border-collapse">
                    <thead className="bg-gray-800 text-white">
                      <tr>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Date
                        </th>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getCurrentPageData(cancelData, currentPageCancel).map(
                        (item, index) => (
                          <tr key={index} className="hover:bg-gray-100">
                            <td className="border-b border-black px-6 py-3">
                              {new Date(item.date).toLocaleDateString()}
                            </td>
                            <td className="border-b border-black px-6 py-3">
                              {item.count}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => handlePagination("prev", "cancel")}
                      disabled={currentPageCancel === 1}
                      className="px-4 py-2 bg-gray-800 text-white rounded-l-md"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 bg-gray-200 text-black">
                      {currentPageCancel} / {totalPages(cancelData)}
                    </span>
                    <button
                      onClick={() => handlePagination("next", "cancel")}
                      disabled={currentPageCancel === totalPages(cancelData)}
                      className="px-4 py-2 bg-gray-800 text-white rounded-r-md"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>

              {/* Tabella per gli ordini in sospeso */}
              <div className="w-full md:w-1/2 lg:w-1/4">
                <div className="overflow-x-auto mb-8 shadow-lg rounded-lg border border-gray-900 bg-white">
                  <h3 className="text-xl font-semibold mb-4 px-6">
                    Pending Orders
                  </h3>
                  <table className="min-w-full table-auto border-collapse">
                    <thead className="bg-gray-800 text-white">
                      <tr>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Date
                        </th>
                        <th className="border-b-4 border-black px-6 py-3 text-left">
                          Count
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {getCurrentPageData(pendingData, currentPagePending).map(
                        (item, index) => (
                          <tr key={index} className="hover:bg-gray-100">
                            <td className="border-b border-black px-6 py-3">
                              {new Date(item.date).toLocaleDateString()}
                            </td>
                            <td className="border-b border-black px-6 py-3">
                              {item.count}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={() => handlePagination("prev", "pending")}
                      disabled={currentPagePending === 1}
                      className="px-4 py-2 bg-gray-800 text-white rounded-l-md"
                    >
                      Previous
                    </button>
                    <span className="px-4 py-2 bg-gray-200 text-black">
                      {currentPagePending} / {totalPages(pendingData)}
                    </span>
                    <button
                      onClick={() => handlePagination("next", "pending")}
                      disabled={currentPagePending === totalPages(pendingData)}
                      className="px-4 py-2 bg-gray-800 text-white rounded-r-md"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Popup per visualizzare il grafico selezionato */}
      {selectedChart && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-4xl relative">
            <button
              onClick={handleClosePopup}
              className="absolute top-2 right-2 text-gray-700 text-xl"
            >
              ×
            </button>
            <h2 className="text-3xl font-semibold mb-4">
              Detailed {selectedChart}
            </h2>
            <div className="w-full h-64">
              {selectedChart === "purchase" && (
                <Line
                  data={createChartData(
                    purchaseData,
                    "Number of Purchases",
                    "#4CAF50",
                    "rgba(76, 175, 80, 0.2)"
                  )}
                  options={chartOptions}
                />
              )}
              {selectedChart === "error" && (
                <Line
                  data={createChartData(
                    errorData,
                    "Number of Errors",
                    "#F44336",
                    "rgba(244, 67, 54, 0.2)"
                  )}
                  options={chartOptions}
                />
              )}
              {selectedChart === "update" && (
                <Line
                  data={createChartData(
                    updateData,
                    "Number of Updates",
                    "#2196F3",
                    "rgba(33, 150, 243, 0.2)"
                  )}
                  options={chartOptions}
                />
              )}
              {selectedChart === "cancel" && (
                <Line
                  data={createChartData(
                    cancelData,
                    "Number of Cancellations",
                    "#FFEB3B",
                    "rgba(255, 235, 59, 0.2)"
                  )}
                  options={chartOptions}
                />
              )}
              {selectedChart === "pending" && (
                <Line
                  data={createChartData(
                    pendingData,
                    "Number of Pending Orders",
                    "#9E9E9E",
                    "rgba(158, 158, 158, 0.2)"
                  )}
                  options={chartOptions}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default MonitorPurchasePage;
